
import {ProjectElement}                    from '@/models/project/project.element';

import {Project}                           from '@/models/project';
import {Logger}                            from "@/util/logger";
import {fromMySQLDate}                     from "@/util/util";
import {LocoQueryService}                  from "@/services/loco.query.service";

import {ProjectElementFactory}             from "@/models/project.element.factory";
import {ProjectElementType}                from "@/models/project/project.element.type";
import {TaskUpdate}                        from "@/models/task.update";
import {TaskLocomotiveModelCreate}         from "@/models/task.locomotive.model.create";
import {TaskProgressService}               from "@/services/task.progress.service";
import {TaskLocomotiveModelCreateFailed}   from "@/models/task.locomotive.model.create.failed";
import {TaskLocomotiveModelCreateComplete} from "@/models/task.locomotive.model.create.complete";
import {OperationalMeasureDialogParams}          from '@/gui/dialogs/operational.measure.dialog.params';
import {LineSimElement}                    from '@/models/project/line.sim.element';


import { v4 as uuidv4 }                    from 'uuid';
import {CorridorGroupElement} from "@/models/project/corridor.group.element";

/**
 *
 * @class OperationalMeasureModelCreator
 *
 * helper to take the parameters from a Operational Measure creation dialog and do
 * all the necessary work to actually create the model and then add it as a leaf to
 * the Operational Measures branch in the user's current project.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    William Au
 * @date      February 2022
 *
 */
export class OperationalMeasureModelCreator {

  /** the user's current project */
  private _project: Project;

  /** standard constructor */
  constructor(
    project: Project
  ) {

    this._project                   = project;
    let ommc: OperationalMeasureModelCreator = this;

    Logger.debug('[OperationalMeasureModelCreator] adding new Operational Measure Group...');

    /* go through a dialog to get the name and parameters for the model */

    ommc._project.services.modals.open(
      'new-opmeasure-modal',
      (value: OperationalMeasureDialogParams, resolve, reject) => {

        let err: string = ommc.validate(value);

        if (err != '') {

          reject(err);
        }

        resolve(value);
      },
      (value: OperationalMeasureDialogParams) => {

        ommc.create(value);

      });

  }

  /** the dialog parameters wer accepted, time to create a model! */
  create(value: OperationalMeasureDialogParams) {

    let ommc: OperationalMeasureModelCreator = this;
    let loco: LocoQueryService      = ommc._project.services.locoquery;

    /* who is the user? */
    let userid: string = ommc._project.services.auth.userOfSession.username;

    /* if they gave the dates backwards, reverse them */

    let sd: Date = fromMySQLDate(value.start);
    let ed: Date = fromMySQLDate(value.end);

    if (ed.getTime() < sd.getTime()) {

      /* they are backwards */

      Logger.debug(`[OperationalMeasureModelCreator] create - start/end are backwards, flipping...`);

      let tmp: string = value.start;
      value.start     = value.end;
      value.end       = tmp;

    }

    /*
     * we expect to generate a "fake simulation result"; i.e. an input scenario and an output
     * train movement; the simulated movement of trains through the rail network, based on that
     * scenario.  But in this case, both input and output are reconstructed from historical
     * train operational data (exported from CN to BigQuery).  So it's real train movements,
     * that actually happened.
     *
     * We do it as a fake simulation though, so we can work with the result in a similar way
     * to how we already deal with scenarios and train movements throughout the GUI.
     *
     * The impact of this is that we have to choose where to store the scenario and movement;
     * normally a simulation would store the results under the scenario node. But there is
     * no "simulation" happening here, so instead we'll store the fake scenario and movement
     * under the project path, and then in "historical/stringlines/" and the remainder of the
     * path should just be the unique file name...made unique by including corridors and start/end
     * in the filename.
     *
     */

    const uuid: string           = uuidv4();
    const  projectFolder: string = this._project.dirname();
    let folder: string           = projectFolder + `/historical/stringlines/${uuid}/`;

    /* add the new child model */

    Logger.debug(`[OperationalMeasureModelCreator] create - new Operational Measure model (${userid}): `, value);

    const tasks: TaskProgressService = ommc._project.services.taskMonitor;
    const taskId: string             = TaskUpdate.generateId();

    tasks.progress(new TaskLocomotiveModelCreate(taskId, `Creating (might take a few minutes): ${value.corridor} ...`, 1.0));

    /* start the creation of the model... */

    loco.historicalStringline(value.corridor, value.start, value.end, folder).then(
      (result: {scenario: string, movement: string}) => {

        Logger.debug('[OperationalMeasureModelCreator] received fake blobs: ', result);

        let group: ProjectElement = ommc._project.operationalMeasures();

        /* create a corridor group to file this one under... */

        /* look for an existing child corridor of the same name, if we don't find it...then create it. */

        let cg: CorridorGroupElement = null;

        if (Array.isArray(group._children)) {

          const peers: ProjectElement[] = group._children;

          for (const peer of peers) {

            if (peer._label === value.corridor) {

              /* we found it, was already created previously */

              cg = peer as CorridorGroupElement;

              Logger.debug(`[OperationalMeasureModelCreator] using existing corridor (${value.corridor}).`);

            }

          }

        }

        if (!cg) {

          /* this corridor doesn't exist yet ... */

          Logger.debug(`[OperationalMeasureModelCreator] first time create on corridor (${value.corridor}).`);

          cg = ProjectElementFactory.fromObject(
            ProjectElementType.CorridorGroup,
            {
              project: this._project,
              id: '',
              name: value.corridor,
              parent: group,
              publish: true
            }) as CorridorGroupElement;
        }

        /* add the fake result ... */

        Logger.debug('[OperationalMeasureModelCreator] adding fake result...');

        const el: LineSimElement = ProjectElementFactory.fromObject(
          ProjectElementType.LineSimResultCalculated,
          {
            project: this._project,
            id:      '',
            input:   result.scenario,
            output:  result.movement,
            name:    value.corridor,
            start:   value.start,
            end:     value.end,
            parent:  cg,
            publish: true
          }) as LineSimElement;

        ommc._project.expand([group]);

        ommc._project.expand([cg]);

        el.save();

        tasks.progress(new TaskLocomotiveModelCreateComplete(taskId, `[${value.corridor}] Stringline ready!`));

        Logger.debug('[OperationalMeasureModelCreator] COMPLETE!');

      },
      (error) => {

        /* track the error */

        tasks.progress(new TaskLocomotiveModelCreateFailed(taskId, error));

        /* log it */

        Logger.error('[OperationalMeasureModelCreator] create - could not do first time calculation model: ', error);

      });

  }

  /** validate the parameters we got from the dialog */
  validate(value: OperationalMeasureDialogParams): string {

      let ommc: OperationalMeasureModelCreator = this;
      let parent: ProjectElement      = ommc._project.locomotiveModels();
      let corridor: string            = value.corridor;

      Logger.debug('[OperationalMeasureModelCreator] (new operational measure) validating: ', name);

      /* is it a valid name? */

      if (corridor.trim() === '') {

        return 'Corridor can not be empty.';
      }

      /* do we already have a model of this name? */

      if (Array.isArray(parent._children)) {

        const peers: ProjectElement[] = parent._children;

        for (const peer of peers) {

          /*
          if (peer._label === corridor) {

            return 'Corridor already exists in this operational model.';
          }
           */

        }
      }

      if (value.start === '') {

        return 'Must set start date';
      }

      if (value.end === '') {

        return 'Must set end date';
      }

      /* its golden! */

      value.corridor = corridor;

      return '';
    }

}
